import { useState, useEffect, useRef, useContext } from 'react';
import EnvContext from '../../EnvContext';

const ARRAY_SIZE = 20;

export async function fetchStudyPages(url, distance, postalCode) {
  return fetch(url).then(
    res => res.json()
  ).then(results => {
    // Filter data by distance from the postal code
    function filterData(data) {
      const locationFilter = data.filter(study => {
        return study.distance <= Number.parseInt(distance) || !!study.landingcoming;
      })
      return locationFilter;
    }

    const { data } = results;
    const filteredData =  postalCode && distance ? filterData(data) : data;
    const hasMoreStudies = filteredData.length === ARRAY_SIZE;
    return { hasMoreStudies, data: filteredData }
  });
}

export async function fetchProtocolPages(url) {
  return fetch(url).then(
    res => res.json()
  ).then(results => {
    const { data } = results;
    const hasMoreStudies = data.length === ARRAY_SIZE;
    return { hasMoreStudies, data }
  });
}


function buildUrl(urlValues, pageCount, type, apiBaseUrl) {
  let count = pageCount === 0 ? pageCount : pageCount * ARRAY_SIZE;
  const urlPath = type === 'protocol' ? '/protocolLandingPages/nearbyPages' : '/studies/getNearbyStudies'
  let apiUrl = `${apiBaseUrl}${urlPath}?`;
  urlValues.forEach(value => {
    const { property, label } = value;
    if (!!property) {
      apiUrl += `&${label}=${property}`;
    }
  })
  apiUrl += `&limit=${ARRAY_SIZE}&from=${count}`;
  return apiUrl;
}

async function loadItems(urlValues, pageCount, searchValues, hasNextPage, apiBaseUrl) {
    const distance = searchValues.find(element => element.label === "distance").property;
    const postalCode = searchValues.find(element => element.label === "postalCode").property;
    const { studyCount, plpCount } = pageCount;
    // create the fetch urls
    const studyURL = buildUrl(urlValues, studyCount, 'study', apiBaseUrl);
    const protocolURL = buildUrl(urlValues, plpCount, 'protocol', apiBaseUrl);

    const res = {
      protocolStudies: [],
      studyPages: [],
      studyCount,
      hasNextPage,
      plpCount,
    }

    // default fetching studies to false since we want to fetch protocol pages first
    // this will delay the study search but since we may have more protocol pages than the limit of 20
    // we only want to fetch studies if no more protocol pages exist

    let fetchStudies = false;
    // we want skip the protocol fetch if we know there are no more
    if (hasNextPage.hasMoreProtocolPages) {
      try {
        const plpResults = await fetchProtocolPages(protocolURL);
        if (plpResults) {
          if (plpResults.hasMoreStudies) {
            res.plpCount +=1;
          } else {
            fetchStudies = true;
          }
          res.hasNextPage.hasMoreProtocolPages = plpResults.hasMoreStudies;
          res.protocolStudies = plpResults.data;
        }
      } catch (e) {
        fetchStudies = true;
        res.hasNextPage.hasMoreProtocolPages = false;
        res.protocolStudies = [];
      }
    } else {
      fetchStudies = true;
    }

    // since there are no more protocol pages to fetch, now start fetching studies
    if (fetchStudies) {
      const studyResults = await fetchStudyPages(studyURL, distance, postalCode)
      if (studyResults) {
        res.studyPages = studyResults.data;
        if (studyResults.hasMoreStudies) {
          res.studyCount +=1;
        }
        res.hasNextPage.hasMoreStudies = studyResults.hasMoreStudies;
      }
    }
    return res;
}

export function useLoadItems(props) {
  const { apiBaseUrl } = useContext(EnvContext);
  const defaultValues =  [
    {property: "us", label: "countryCode"},
    {property: "", label: "postalCode"},
    {property: "", label: "indicationId"},
    {property: "", label: "distance"}
  ];
  // Load some defaults for search if navigating directly
  const urlValues = props.history && props.history.location && props.history.location.state && props.history.location.state.urlValues || defaultValues;

  const [loading, setLoading] = useState(false);
  const [studies, setStudies] = useState([]);
  const [protocolLandingPages, setProtocolLandingPages] = useState([]);
  const [error, setError] = useState();
  const [hasNextPage, setHasNextPage] = useState({ hasMoreStudies: true, hasMoreProtocolPages: true});
  const [pageCount, setPageCount] = useState({ studyCount: 0, plpCount: 0});
  const initial = useRef(urlValues);

  useEffect(() => {
    if (urlValues !== initial.current) {
      window.scrollTo(0,0);
      setStudies([]);
      setProtocolLandingPages([])
      setPageCount({studyCount: 1, plpCount: 1}, loadMore(true));
    }
  },[JSON.stringify(urlValues)]);

  async function loadMore(reset = false) {
    setLoading(true);
    try {
      // if reset is true we want to reset the page chunks and next page booleans
      let pageCounter = pageCount;
      let hasNextPageInfo = hasNextPage;
      if (reset) {
        pageCounter = { studyCount: 0, plpCount: 0 };
        hasNextPageInfo = { hasMoreStudies: true, hasMoreProtocolPages: true}
      }
  
      const { studyPages, hasNextPage: newHasNextPage, protocolStudies, studyCount, plpCount } = await loadItems(urlValues, pageCounter, urlValues, hasNextPageInfo, apiBaseUrl);
      
      setPageCount({studyCount, plpCount});
      setHasNextPage(newHasNextPage);
      setProtocolLandingPages((current) => [...current, ...protocolStudies])
      setStudies((current) => [...current, ...studyPages]);
    } catch (err) {
      setError(err);
    } finally {
      setLoading(false);
    }
  }

  return { loading, studies, protocolLandingPages, hasNextPage, error, loadMore };
}